<style>
  @import "../account.scss";
</style>

<script>
import Vue from 'vue'
import Vuelidate from 'vuelidate'
import { email, minLength, required } from 'vuelidate/lib/validators'
import api from '../../../actions/api.actions'
import jwtVerification from '../../../utils/jwt-verification'
import stageAccessKeys from '../../../data/stageUserAccesskeys'
import prodAccessKeys from '../../../data/prodUserAccessKeys'

Vue.use(Vuelidate)

export default {
  name: 'AdminLogin',
  data () {
    return {
      emailValue: '',
      showError: '',
      passwordValue: '',
      email: '',
      password: '',
      successFullLogin: false,
      jwtExpiryTime: '',
      currentTime: '',
      jwtVerificationKeys: '',
      expireJwt: '',
      userSignIn: false
    }
  },

  validations: {
    emailValue: {
      required,
      email
    },

    passwordValue: {
      required,
      minLength: minLength(6)
    }

  },

  created () {
    this.successFullLogin = !!this.$store.state.configuration.userAccessToken
    if (this.$store.state.configuration.userAccessToken) {
      this.checkJwtExpiry()
    }
    this.jwtVerificationKeys = process.env.VUE_APP_ADMIN_KEYS_ENV === 'production' ? prodAccessKeys : stageAccessKeys
  },

  methods: {
    /**
       * This method will update the time and check the expiry for jwt
       */
    updateTime () {
      let date = new Date()
      this.currentTime = Math.floor(date.getTime() / 1000.0)
      this.jwtExpiryTime = jwtVerification.validateJwtExpiry()
      if (!this.userSignIn) {
        if (this.jwtExpiryTime && this.currentTime < this.jwtExpiryTime) {
          this.successFullLogin = true
        } else {
          this.successFullLogin = false
          clearInterval(this.currentTime)
          this.resetValues()
        }
      }
    },

    /**
       * This method call the updateTime to check jwt expiry
       */
    checkJwtExpiry () {
      setInterval(this.updateTime, 1000)
      this.updateTime()
    },

    /**
       * This method allows the admin to sign in
       */
    signIn () {
      this.userSignIn = false
      if (this.passwordValue && this.emailValue) {
        this.getAdminAccess()
      } else {
        this.showError = true
      }
    },

    /**
       * This method reset the jwt and user name values
       */
    resetValues () {
      this.$store.dispatch('updateUserAccessToken', '')
      this.$store.dispatch('adminUserName', null)
      const configDetails = JSON.parse(localStorage.getItem('configDetails'))
      if (configDetails) {
        configDetails.userAccessToken = ''
        configDetails.adminUserName = null
        localStorage.setItem('configDetails', JSON.stringify(configDetails))
      }
    },

    /**
       * This method allows the admin to sign out
       */
    signOut () {
      this.passwordValue = ''
      this.emailValue = ''
      this.successFullLogin = false
      this.userSignIn = true
      this.resetValues()
      clearInterval(this.currentTime)
    },

    /**
       * This method verifies the jwt
       * @param response
       */
    verifyJwt (response) {
      if (response) {
        jwtVerification.verifyJwtKey(response.startAccessJwt, this.jwtVerificationKeys).then(() => {
          this.checkJwtExpiry()
          this.showError = false
        }).catch(() => {
          this.showError = true
          this.successFullLogin = false
          this.resetValues()
        })
      }
    },

    /**
       * This method calls the user access api
       * @returns {Promise<void>}
       */
    async getAdminAccess () {
      const adminDetails = {
        'email': this.emailValue,
        'pwd': btoa(this.passwordValue)
      }
      try {
        const response = await api.getUserAccess(adminDetails)
        this.$store.commit('updateUserAccessToken', response)
        this.$store.commit('adminUserName', adminDetails.email)
        const configDetails = JSON.parse(localStorage.getItem('configDetails'))
        if (configDetails) {
          configDetails.userAccessToken = response
          configDetails.adminUserName = adminDetails.email
          localStorage.setItem('configDetails', JSON.stringify(configDetails))
        }
        this.verifyJwt(response)
      } catch (error) {
        if (error.response) {
          this.showError = true
          this.successFullLogin = false
        }
      }
    }
  }
}
</script>

<template>
  <div
    id="admin-login"
    class="pt-5 pb-4"
  >
    <div
      v-if="showError"
      class="alert alert-danger mx-auto col-sm-12 col-md-10"
      role="alert"
    >
      {{ $t ('errorMessages.SIGN_IN_ERROR') }}
    </div>
    <div
      v-if="!successFullLogin"
      class="row justify-content-center"
    >
      <h5> {{ $t ('label.adminSignIn') }}</h5>
      <div class="col-md-12 my-5">
        <div class="src-user-icon">
          <i class="fa fa-user-circle-o text-primary fa-5x" />
        </div>
        <form
          class="mt-5"
        >
          <div class="form-group mx-auto col-sm-8 col-md-8">
            <input
              id="input-account-email"
              v-model="emailValue"
              aria-describedby="emailHelp"
              class="form-control"
              name="email"
              placeholder="Admin user name"
              type="email"
            >
            <small
              v-if="!$v.emailValue.email"
              id="emailErrValid"
              class="form-text text-danger"
            >
              {{ $t ('label.validEmail') }}
            </small>
          </div>
          <div class="form-group mx-auto col-sm-8 col-md-8">
            <input
              id="input-account-password"
              v-model="passwordValue"
              aria-describedby="passwordHelp"
              class="form-control"
              name="password"
              placeholder="Password"
              type="password"
            >
            <small
              v-if="!$v.passwordValue.minLength"
              id="pswdValidReq"
              class="form-text text-danger"
            >
              {{ $t ('label.validPassword') }}
            </small>
          </div>
          <small
            id="passwordHelpBlock"
            class="form-text text-muted text-center mt-5"
          >
            {{ $t ('label.casePassword') }}
          </small>
          <div class="text-center mt-4">
            <a href="javascript:;"> {{ $t ('label.forgotPassword') }}</a>
          </div>
          <div class="text-center col-md-8 col-sm-8 mx-auto mt-4">
            <button
              class="btn btn-primary btn-block"
              type="button"
              @click="signIn()"
            >
              {{ $t ('label.signIn') }}
            </button>
          </div>
        </form>
      </div>
    </div>
    <div
      v-if="successFullLogin"
      class="mt-6"
    >
      <div class="src-user-icon my-5">
        <i class="fa fa-user-circle-o text-primary fa-5x" />
      </div>
      <div
        id="logged-in-page"
        class="row mt-3 justify-content-center"
      >
        <h3 class="col-md-12 text-center text-lg font-weight-bold">
          {{ $t ('label.myAccount') }}
        </h3>
        <small
          id="email-account-value"
          class="col-md-12 text-center mt-3 mb-5 text-muted"
        >
          {{ emailValue || $store.state.configuration.adminUserName }}
        </small>
        <div class="col-md-8 col-sm-8 mx-auto mt-5 mb-5">
          <button
            id="account-sign-out-button"
            class="btn btn-primary btn-block"
            type="button"
            @click="signOut()"
          >
            {{ $t ('label.signOut') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
